import * as React from "react";
import { PaymentTypes } from "@grandvisionhq/api-client";
import { useAccount } from "@grandvisionhq/auth";
import { Context } from "@grandvisionhq/carts-v2";
import { Util, toast } from "@grandvisionhq/common";
import { useLink, useLocation } from "@grandvisionhq/frk-routing";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import { useSync } from "@grandvisionhq/lib-utils-w3c";
import { WithUserData } from "@grandvisionhq/my-account";
import { Hooks, usePaymentsUIContext, PaymentMethod, PaymentFlow, PaypalPaymentEventsProvider } from "@grandvisionhq/payments";
import { useDispatch, useSelector } from "@grandvisionhq/state";
import { setDonation } from "../../actions.js";
import { pushDonationSelectEventToDataLayer } from "../../data-layer.js";
import { useCreateOrder } from "../../hooks/orders/hooks/orders/use-create-order.js";
import { useCreateOrderDraft } from "../../hooks/orders/hooks/orders/use-create-order-draft.js";
import { useRedirectToOrderComplete } from "../../hooks/orders/hooks/orders/use-redirect-to-order-confirm-page.js";
import { ApplePaySessionErrors, CreateOrderErrors } from "../../mappers/mappers/errors-mapping.js";
import { getSelectedDonation } from "../../selectors.js";
import { selectPaymentMethod } from "./actions.js";
import { StepPayment } from "./StepPayment.js";
import { getBrowserInfo } from "../utils/StepPayment/utils/get-browser-info.js";
/* empty css                 */
import { useGetQueryParams } from "../../utils/utils/get-query-params.js";
import { useGetLabels } from "../hooks/StepPayment/hooks/use-get-labels.js";
import { useApplePay } from "../../modules/ExpressCheckout/hooks/apple-pay/modules/ExpressCheckout/hooks/apple-pay/use-apple-pay.js";
import { useHandleQueryParams } from "../hooks/StepPayment/hooks/use-handle-query-params.js";
import { CheckoutStepErrorType } from "../../models/checkout/models/checkout/checkout-step-error-type.js";
import { useHandleQrFormUpdates } from "../hooks/StepPayment/hooks/use-handle-qr-form-updates.js";
import { useCheckoutSettings } from "../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const StepPaymentContainer = ({
  onPrevStep,
  selfUrl,
  updateCheckoutStatus,
  onNextStep
}) => {
  const { status, cartId, type: paymentType } = useGetQueryParams();
  const [labels] = useGetLabels();
  const redirectToOrderComplete = useRedirectToOrderComplete();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState();
  const [payCartWithApplePay] = useApplePay({ isExpressPayment: false });
  const isApplePayActive = Hooks.useIsApplePayActive();
  const authState = useAccount();
  const createOrderDraft = useCreateOrderDraft();
  const [createOrder, { orderResponse, error: createOrderError, paymentFlow, setPaymentFlow }] = useCreateOrder();
  const [isLoading, setIsLoading] = React.useState(false);
  const [updateUrlQueryParam, deleteQueryParam] = useHandleQueryParams();
  const { href: homeHref } = useLink({ route: "home" }, { replace: true }) || {};
  const { onClick: navigateToCart } = useLink({ route: "cart.overview" }, { replace: true }) || {};
  const broadCastChannel = useSync("new-cart-created").getChannel();
  broadCastChannel.onmessage = () => {
    navigateToCart?.();
  };
  const { replace } = useLocation();
  const { cart, refetch } = Context.useCart();
  const [_, { reset }] = useCartStorage();
  const { isQrCodeModalOpen, setCurrentCardBrand, googlePayToken } = usePaymentsUIContext();
  const prevIsQrCodeModalOpenValue = Util.usePrevious(isQrCodeModalOpen);
  const dispatch = useDispatch();
  const selectedDonation = useSelector(getSelectedDonation);
  React.useEffect(() => {
    if (paymentType === PaymentMethod.APPLE_PAY && !isApplePayActive) {
      toast.failed(ApplePaySessionErrors.ApplePaySessionUnsupported);
      deleteQueryParam(["type"]);
    }
  }, [deleteQueryParam, isApplePayActive, paymentType, updateUrlQueryParam]);
  React.useEffect(() => {
    if (status === CheckoutStepErrorType.ProductPriceUpdated) {
      refetch();
    }
  }, [status, refetch]);
  React.useEffect(() => {
    if (cart?.checkoutState == "Finalized") {
      reset();
      homeHref && replace(homeHref);
    }
  }, [cart?.checkoutState, homeHref, replace, reset]);
  React.useEffect(() => {
    if (createOrderError) {
      const checkoutStatus = createOrderError === CreateOrderErrors.PRODUCT_PRICE_UPDATED ? CheckoutStepErrorType.ProductPriceUpdated : CheckoutStepErrorType.Failed;
      updateCheckoutStatus && updateCheckoutStatus(checkoutStatus);
    }
  }, [createOrderError, updateCheckoutStatus]);
  React.useEffect(() => {
    if (prevIsQrCodeModalOpenValue !== isQrCodeModalOpen) {
      if (cartId && !isQrCodeModalOpen) {
        setPaymentFlow(PaymentFlow.UNDEFINED);
      }
    }
  }, [cartId, isQrCodeModalOpen, prevIsQrCodeModalOpenValue, setPaymentFlow]);
  React.useEffect(() => {
    if (status && (status === PaymentTypes.PaymentResult.Cancelled || status === PaymentTypes.PaymentResult.Refused || status === PaymentTypes.PaymentResult.Error || status === PaymentTypes.PaymentResult.InternalError)) {
      updateCheckoutStatus && updateCheckoutStatus(CheckoutStepErrorType.Failed);
    }
  }, [status, authState]);
  const { paymentMethodLogos } = useCheckoutSettings();
  const handleOnPaymentMethodSelected = React.useCallback(
    (paymentMethodConfig) => {
      setSelectedPaymentMethod(paymentMethodConfig);
      dispatch(
        selectPaymentMethod({
          type: paymentMethodConfig.type,
          provider: paymentMethodConfig.provider
        })
      );
      setCurrentCardBrand(void 0);
      updateUrlQueryParam({ type: paymentMethodConfig.type });
      if (selectedPaymentMethod?.type && selectedPaymentMethod?.type !== paymentMethodConfig.type && status !== "") {
        updateCheckoutStatus && updateCheckoutStatus("");
        deleteQueryParam(["status"]);
      }
    },
    [
      deleteQueryParam,
      dispatch,
      selectedPaymentMethod?.type,
      setCurrentCardBrand,
      status,
      updateCheckoutStatus,
      updateUrlQueryParam
    ]
  );
  const handleCreateOrder = React.useCallback(
    async (selfUrl2, paymentDetails) => {
      if (typeof cartId === "string" && selectedPaymentMethod) {
        setIsLoading(true);
        const orderPayload = await createOrderDraft({
          paymentDetails,
          selectedPaymentMethod,
          returnUrl: `${selfUrl2}/api/payment-callback?type=${selectedPaymentMethod.type}`,
          overwritePersonalDetails: true
        });
        const createOrderResult = await createOrder(orderPayload);
        if (createOrderResult && createOrderResult.success && !createOrderResult.redirect && createOrderResult.resultType !== "QRCODE") {
          try {
            redirectToOrderComplete({
              cartId: createOrderResult?.cartId,
              orderData: createOrderResult?.orderDataToken
            });
          } catch (error) {
            console.error(error);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      }
    },
    [cartId, createOrder, createOrderDraft, redirectToOrderComplete, selectedPaymentMethod]
  );
  const handleDonation = React.useCallback(
    (donationPayload) => {
      pushDonationSelectEventToDataLayer(donationPayload.foundationKey, donationPayload.amount);
      dispatch(
        setDonation({
          paymentMethod: donationPayload.paymentMethod,
          amount: donationPayload.amount ?? null,
          key: donationPayload.foundationKey
        })
      );
    },
    [dispatch]
  );
  const createGooglePayOrder = React.useCallback(
    (googlePayToken2) => {
      handleCreateOrder(selfUrl, {
        isValid: true,
        type: PaymentMethod.GOOGLE_PAY,
        data: { googlePayToken: googlePayToken2, browserInfo: { ...getBrowserInfo() } }
      });
    },
    [handleCreateOrder, selfUrl]
  );
  React.useEffect(() => {
    if (googlePayToken) {
      createGooglePayOrder(googlePayToken);
    }
  }, [googlePayToken]);
  const onPaypalError = () => {
    toast.failed("checkout.errors.general.message");
  };
  const createPaypalOrder = React.useCallback(
    (paypalOrderId) => {
      handleCreateOrder(selfUrl, {
        isValid: true,
        type: PaymentMethod.PAYPAL,
        data: {
          referenceId: paypalOrderId
        }
      });
    },
    [handleCreateOrder, selfUrl]
  );
  const paypalPaymentEvents = Hooks.useCreatePaypalPaymentEvents({
    hasSubscription: Boolean(cart?.hasSubscription),
    cartId: typeof cartId === "string" ? cartId : "",
    onPaymentAuthorization: createPaypalOrder
  });
  const isPaymentStepValid = React.useCallback((paymentDetails) => {
    const isValid = paymentDetails && paymentDetails.isValid;
    if (!isValid) {
      toast.failed("checkout.errors.form.payment.details");
    }
    return Boolean(isValid);
  }, []);
  const handleNextClick = React.useCallback(
    ({ paymentDetailsConfig } = {}) => {
      if (isPaymentStepValid(paymentDetailsConfig)) {
        updateCheckoutStatus && updateCheckoutStatus("");
        if (selectedPaymentMethod?.type === PaymentMethod.APPLE_PAY) {
          payCartWithApplePay?.();
        } else {
          paymentDetailsConfig ? handleCreateOrder(selfUrl, paymentDetailsConfig) : handleCreateOrder(selfUrl);
        }
      }
    },
    [
      isPaymentStepValid,
      selectedPaymentMethod?.type,
      updateCheckoutStatus,
      payCartWithApplePay,
      handleCreateOrder,
      selfUrl
    ]
  );
  const handlePreviousClick = React.useCallback(() => {
    onPrevStep && onPrevStep();
  }, [onPrevStep]);
  const paymentMethodType = selectedPaymentMethod?.type ?? "swish";
  const [qrCodeFormData] = useHandleQrFormUpdates({
    ...orderResponse ? {
      qrPaymentConfig: {
        qrCodeExpiresIn: orderResponse.qrCodeExpiresIn,
        qrCodeUrl: orderResponse.qrCodeUrl,
        redirectUrl: orderResponse.redirectUrl
      }
    } : {},
    paymentFlow,
    selfUrl,
    paymentMethodType,
    paymentMethodLogos,
    orderId: paymentFlow === PaymentFlow.QR_CODE ? orderResponse?.orderId : void 0,
    orderDataToken: orderResponse?.orderDataToken
  });
  const onHandlePayment = React.useCallback(
    (selectedPaymentConfig) => {
      selectedPaymentConfig && updateUrlQueryParam({ type: selectedPaymentConfig.type });
      onNextStep();
      handleNextClick({ paymentDetailsConfig: selectedPaymentConfig });
    },
    [updateUrlQueryParam, onNextStep, handleNextClick]
  );
  return /* @__PURE__ */ React.createElement(
    PaypalPaymentEventsProvider,
    {
      ...paypalPaymentEvents,
      onClick: onNextStep,
      orderType: cart?.hasSubscription ? "subscription" : "regular",
      onError: onPaypalError
    },
    /* @__PURE__ */ React.createElement(
      StepPayment,
      {
        onNextClick: handleNextClick,
        onPrevClick: handlePreviousClick,
        onHandlePayment,
        qrCodeForm: qrCodeFormData,
        selectedPaymentMethod: paymentType ?? "",
        onPaymentMethodSelected: handleOnPaymentMethodSelected,
        selectedDonation: selectedDonation?.amount,
        onDonationSelected: handleDonation,
        cartId,
        labels,
        isOrderInProgress: isLoading,
        totalPrice: cart?.prices.totalPrice
      }
    )
  );
};
const StepPaymentContainerWithUserData = WithUserData(StepPaymentContainer);
export {
  StepPaymentContainerWithUserData as default
};
//# sourceMappingURL=StepPaymentContainer.js.map
